/* eslint-disable camelcase */
import type {
  SkillCategory,
  SkillNeedTemplate_TrainingMatrix_Capacity_Response
} from '@lmc/ts-common'
import { getJSON } from 'common/services/api'
import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'
import type { CapacityTable } from '../components/trainingMatrix/CapacityMatrix'

const GET_CATEGORIES_URL = `/api/list/skill-need-templates/categories`
const GET_CAPACITY_URL = `/api/list/skill-need-templates/training-matrix/capacity`

export type CapacityMatrixResponse =
  SkillNeedTemplate_TrainingMatrix_Capacity_Response

const renderSeparator = (index: number, length: number) => {
  if (index === length - 1) {
    return ''
  }

  return '; '
}

const getColumns = (t: (value: string) => string) => [
  {
    title: t(`skillsAndNeeds.table.skills`),
    dataIndex: 'skills',
    key: 'skills'
  },
  {
    title: t(`skillsAndNeeds.table.needs`),
    dataIndex: 'needs',
    key: 'needs',
    render: (needs: string[]) =>
      needs.map((need, index, initial) => (
        <div key={need}>{`${need}${renderSeparator(
          index,
          initial.length
        )}`}</div>
      ))
  },
  {
    title: t(`skillsAndNeeds.table.carersWithValidSkills`),
    dataIndex: 'carersWithValidSkills',
    key: 'carersWithValidSkills'
  },
  {
    title: t(`skillsAndNeeds.table.residentsWithNeeds`),
    dataIndex: 'residentsWithNeeds',
    key: 'residentsWithNeeds'
  }
]

export const getResidentsWithNeeds = (
  needs: CapacityMatrixResponse['content'][0]['needs']
) => {
  const residentsWithNeeds: string[] = []

  needs.forEach((need) => {
    need.residents.forEach((resident) => {
      if (!residentsWithNeeds.includes(resident)) {
        residentsWithNeeds.push(resident)
      }
    })
  })

  return residentsWithNeeds.length
}

export const getDataSource = (
  data: CapacityMatrixResponse | undefined
): CapacityTable[] => {
  const dataSource = data?.content?.map((element) => ({
    skills: element.skill.name,
    needs: element.needs.map((need) => need.name),
    carersWithValidSkills: element.skill.carers.length,
    residentsWithNeeds: getResidentsWithNeeds(element.needs)
  }))

  return dataSource || []
}

const useCapacityMatrix = () => {
  const { data: categoriesData } = useQuery<{ result: SkillCategory[] }>(
    'categories',
    () => getJSON({ url: `${GET_CATEGORIES_URL}` })
  )

  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  // const [categoriesQuery, setCategoriesQuery] = useState('')
  const categoriesQuery = useMemo(() => {
    const categories = selectedCategories?.map((category) => category)

    if (categories.length === 0) {
      return ''
    }

    const categoriesQuery = `?categories=${categories.join(',')}`

    return categoriesQuery
  }, [selectedCategories])
  const onCategoryChange = (value: string[]) => {
    setSelectedCategories(value)
  }

  const { data } = useQuery<{ result: CapacityMatrixResponse }>(
    ['capacity', categoriesQuery],
    () => getJSON({ url: `${GET_CAPACITY_URL}${categoriesQuery}` })
  )

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: { preferredPeopleNominative?: string } } }) =>
      state.global.home.preferredPeopleNominative
  )

  const categories =
    categoriesData?.result?.map((category) => ({
      value: category.id,
      label: category.name
    })) || []
  const capacityMatrixRows = getDataSource(data?.result)

  return {
    categories,
    capacityMatrixRows,
    preferredPeopleNominative,
    onCategoryChange,
    getColumns
  }
}

export default useCapacityMatrix
